import React, { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { CreditCard, ChevronDown, ExternalLink } from 'lucide-react';
import { PAYMENT_OPTIONS } from './data';
import { useLanguage } from './i18n/LanguageContext';
type PayButtonProps = {
  /** 'compact' for tight spots like the navbar, 'full' for content areas. */
  size?: 'compact' | 'full';
  className?: string;
  offsetColor?: string;
  /** Where the menu opens from, relative to the button. */
  align?: 'left' | 'right';
};
export function PayButton({
  size = 'compact',
  className = '',
  offsetColor = 'focus-visible:ring-offset-[#faf0e7]',
  align = 'right'
}: PayButtonProps) {
  const {
    t
  } = useLanguage();
  const shouldReduceMotion = useReducedMotion();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const sizeClassName = size === 'compact' ? 'gap-1.5 px-3 py-2 text-[9px] tracking-[2px] sm:px-4 sm:text-[10px] sm:tracking-[2.5px]' : 'gap-2 px-7 py-3.5 text-[10px] tracking-[3px]';
  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent | TouchEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);
  return <div ref={containerRef} className="relative inline-flex" data-id="element-370" data-mp="1qxu2xli79x0a">
      <motion.button type="button" onClick={() => setOpen(current => !current)} whileHover={{
      y: -1
    }} whileTap={{
      scale: 0.97
    }} aria-label={t.pay.aria} aria-haspopup="menu" aria-expanded={open} className={`metallic-reserve-button inline-flex items-center justify-center font-body font-semibold uppercase text-white outline-none focus-visible:ring-2 focus-visible:ring-champagne ${offsetColor} ${sizeClassName} ${className}`} data-id="element-371" data-mp="1vw3wjroa0pjs">
        <CreditCard className="h-3.5 w-3.5 shrink-0" strokeWidth={2} data-id="element-372" data-mp="26ua8iyzdmm4d" />
        {t.pay.label}
        <ChevronDown aria-hidden="true" className={`h-3 w-3 shrink-0 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} strokeWidth={2.2} data-id="element-373" data-mp="26rxgtu3tgxe4" />
      </motion.button>

      <AnimatePresence data-id="element-374" data-mp="2bla3vzsukw4r">
        {open && <motion.div role="menu" aria-label={t.pay.menuTitle} initial={shouldReduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: -6,
        scale: 0.98
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={shouldReduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        y: -6,
        scale: 0.98
      }} transition={{
        duration: shouldReduceMotion ? 0.12 : 0.2,
        ease: [0.22, 1, 0.36, 1]
      }} className={`absolute top-[calc(100%+8px)] z-[80] w-[262px] origin-top border border-metallic-border bg-warm-white shadow-[0_20px_46px_rgba(44,33,24,0.24)] ${align === 'right' ? 'right-0' : 'left-0'}`} data-id="element-375" data-mp="2bmbek35ybgly">
            <p className="border-b border-brand-border bg-[#faf0e7] px-4 py-2.5 font-body text-[8px] font-semibold uppercase tracking-[2.5px] text-champagne-deep" data-id="element-376" data-mp="2lgqpasatqzrn">
              {t.pay.menuTitle}
            </p>
            <ul className="divide-y divide-brand-border" data-id="element-377" data-mp="2chdjqaptmvc7">
              {PAYMENT_OPTIONS.map(option => {
            const copy = t.pay.options[option.id];
            return <li key={option.id} data-id="element-378" data-mp="22j61lojsq78m">
                    <a role="menuitem" href={option.url} target="_blank" rel="noreferrer" onClick={() => setOpen(false)} className="group flex items-start justify-between gap-3 px-4 py-3 text-left transition-colors hover:bg-[#faf0e7] focus-visible:bg-[#faf0e7] focus-visible:outline-none" data-id="element-379" data-mp="22k7qavtqis8t">
                      <span className="min-w-0" data-id="element-380" data-mp="2w3qx08sqp44k">
                        <span className="flex items-center gap-1.5 font-body text-[11px] font-semibold uppercase tracking-[1.5px] text-dark" data-id="element-381" data-mp="2w2z1exz576ea">
                          {copy.label}
                          <ExternalLink aria-hidden="true" className="h-3 w-3 shrink-0 text-champagne-deep opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100" strokeWidth={2} data-id="element-382" data-mp="2n0zh9wstzm74" />
                        </span>
                        <span className="mt-1 block font-body text-[11px] font-light leading-snug text-soft" data-id="element-383" data-mp="bd9mkkyfvkll">
                          {copy.note}
                        </span>
                      </span>
                      {option.amount && <span className="shrink-0 font-serif text-sm font-light text-champagne-deep" data-id="element-384" data-mp="nu18sfo32inx">
                          {option.amount}
                        </span>}
                    </a>
                  </li>;
          })}
            </ul>
          </motion.div>}
      </AnimatePresence>
    </div>;
}